// 📁 src/LiveMap.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import socket from "./socket";
import axios from "axios";
import "./LiveMap.css";

function formatElapsed(ms) {
  if (ms == null) return "--:--";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371e3;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function bearingTo(from, to) {
  const φ1 = (from.lat * Math.PI) / 180;
  const φ2 = (to.lat * Math.PI) / 180;
  const Δλ = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function cardinalDir(bearing) {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(bearing / 45) % 8];
}

function formatDist(m) {
  if (m < 1000) return `${Math.round(m)}m`;
  return `${(m / 1000).toFixed(1)}km`;
}

export default function LiveMap() {
  // -----------------------------
  // Refs
  // -----------------------------
  const mapRef = useRef(null);
  const googleMap = useRef(null);
  const watchIdRef = useRef(null);
  const markersRef = useRef({});           // playerId -> marker
  const checkpointMarkersRef = useRef({}); // checkpointId -> { marker, collected }
  const gameEndedRef = useRef(false);
  const redirectingRef = useRef(false);
  const userIdRef = useRef(null);
  const gameIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const showToastTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const teamIdRef = useRef(null);
  const gpsCenteredRef = useRef(false); // true after map re-centered on first real GPS fix

  // -----------------------------
  // State
  // -----------------------------
  const [gameInfo, setGameInfo] = useState(null);
  const [userId, setUserId] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const [players, setPlayers] = useState({});
  const [myPosition, setMyPosition] = useState(null);

  const [checkpoints, setCheckpoints] = useState([]);       // full list from server
  const [collectedIds, setCollectedIds] = useState(new Set()); // ids this team collected
  const [leaderboard, setLeaderboard] = useState([]);

  const [toastMessage, setToastMessage] = useState("");
  const [elapsedMs, setElapsedMs] = useState(null);
  const [raceStarted, setRaceStarted] = useState(false);
  const [raceFinished, setRaceFinished] = useState(false);
  const [finishData, setFinishData] = useState(null);       // { rank, elapsedMs }
  const [gameEndResults, setGameEndResults] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  // -----------------------------
  // Sync refs
  // -----------------------------
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { gameIdRef.current = gameInfo?.gameId || null; }, [gameInfo?.gameId]);
  useEffect(() => { teamIdRef.current = gameInfo?.teamId?.toString() || null; }, [gameInfo?.teamId]);

  // -----------------------------
  // Utilities
  // -----------------------------
  const showToast = useCallback((msg, ms = 2500) => {
    setToastMessage(msg);
    if (showToastTimerRef.current) clearTimeout(showToastTimerRef.current);
    showToastTimerRef.current = setTimeout(() => setToastMessage(""), ms);
  }, []);

  // -----------------------------
  // Checkpoint marker helpers
  // -----------------------------
  const renderCheckpointMarker = useCallback((cp, collected) => {
    if (!googleMap.current) return;

    if (checkpointMarkersRef.current[cp._id]) {
      checkpointMarkersRef.current[cp._id].setMap(null);
    }

    let fillColor, strokeColor, scale, labelColor;

    if (cp.type === 'start') {
      fillColor = "#22c55e";   // bright green
      strokeColor = "#14532d";
      scale = 16;
      labelColor = "#fff";
    } else if (cp.type === 'finish') {
      fillColor = collected ? "#6b7280" : "#f59e0b"; // amber until earned
      strokeColor = collected ? "#374151" : "#92400e";
      scale = 16;
      labelColor = "#fff";
    } else {
      // Regular checkpoint
      fillColor = collected ? "#6b7280" : "#3b82f6";
      strokeColor = collected ? "#374151" : "#1e3a8a";
      scale = 13;
      labelColor = collected ? "#ccc" : "#fff";
    }

    const marker = new window.google.maps.Marker({
      map: googleMap.current,
      position: { lat: cp.lat, lng: cp.lng },
      label: {
        text: cp.name,
        color: labelColor,
        fontSize: "11px",
        fontWeight: "bold"
      },
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale,
        fillColor,
        fillOpacity: 1,
        strokeColor,
        strokeWeight: 2,
      },
      title: cp.type === 'start' ? `START: ${cp.name}` : cp.type === 'finish' ? `FINISH: ${cp.name}` : cp.name
    });

    checkpointMarkersRef.current[cp._id] = marker;
  }, []);

  // When map becomes ready, render all checkpoint markers
  useEffect(() => {
    if (!mapReady || !googleMap.current) return;
    checkpoints.forEach(cp => renderCheckpointMarker(cp, collectedIds.has(cp._id)));
  }, [mapReady, checkpoints, collectedIds, renderCheckpointMarker]);

  // -----------------------------
  // Elapsed timer
  // -----------------------------
  useEffect(() => {
    if (!raceStarted || raceFinished) return;

    const startTs = startTimeRef.current ? new Date(startTimeRef.current).getTime() : Date.now();

    elapsedTimerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTs);
    }, 1000);

    return () => clearInterval(elapsedTimerRef.current);
  }, [raceStarted, raceFinished]);

  // -----------------------------
  // Hard reset on mount/unmount
  // -----------------------------
  useEffect(() => {
    googleMap.current = null;
    markersRef.current = {};
    checkpointMarkersRef.current = {};
    gameEndedRef.current = false;
    redirectingRef.current = false;
    gpsCenteredRef.current = false;
    setMapReady(false);

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      if (showToastTimerRef.current) clearTimeout(showToastTimerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      Object.values(markersRef.current).forEach(m => m?.setMap?.(null));
      Object.values(checkpointMarkersRef.current).forEach(m => m?.setMap?.(null));
    };
  }, []);

  // -----------------------------
  // Initial load
  // -----------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const [me, status] = await Promise.all([
          axios.get("/api/me", { withCredentials: true }),
          axios.get("/api/my-approval-status", { withCredentials: true }),
        ]);

        const uid = me.data.user._id;
        setUserId(uid);
        setGameInfo(status.data);
        userIdRef.current = uid;
        gameIdRef.current = status.data?.gameId || null;

        // Restore collected checkpoints from status
        if (status.data?.checkpointsCollected > 0) {
          // We don't have IDs here, leaderboardUpdate will reconcile
        }

        if (status.data?.raceStarted && status.data?.startTime) {
          startTimeRef.current = status.data.startTime;
          setRaceStarted(true);
        }

        if (status.data?.finishTime) {
          setRaceFinished(true);
          setFinishData({
            rank: status.data.rank,
            elapsedMs: new Date(status.data.finishTime) - new Date(status.data.startTime)
          });
        }

        // Seed collected IDs from status so arrow targets are correct from load
        if (Array.isArray(status.data?.checkpointCollectedIds)) {
          setCollectedIds(new Set(status.data.checkpointCollectedIds));
        }

        // Fetch checkpoints directly — don't rely on socket since joinGameRoom
        // may have already been called in the lobby (idempotency blocks re-send)
        if (status.data?.gameId) {
          socket.emit("joinGameRoom", status.data.gameId);

          const cpRes = await axios.get(
            `/api/games/${status.data.gameId}/checkpoints`,
            { withCredentials: true }
          );
          if (Array.isArray(cpRes.data)) setCheckpoints(cpRes.data);
        }
      } catch (err) {
        console.error("Load error:", err);
        window.location.replace("/lobby");
      }
    };
    load();
  }, []);

  // -----------------------------
  // Checkpoint list from server
  // -----------------------------
  useEffect(() => {
    const handler = (list) => {
      if (!Array.isArray(list)) return;
      setCheckpoints(list);
    };
    socket.on("checkpointList", handler);
    return () => socket.off("checkpointList", handler);
  }, []);

  // -----------------------------
  // Checkpoint collected (any team)
  // -----------------------------
  useEffect(() => {
    const handler = ({ teamId, teamName, checkpointId, checkpointName }) => {
      if (redirectingRef.current) return;

      showToast(`${teamName}: ${checkpointName}`, 2000);

      // If it's our team, track collected ID so the arrow updates
      if (teamId && teamId === teamIdRef.current) {
        setCollectedIds(prev => {
          const next = new Set(prev);
          next.add(checkpointId);
          return next;
        });
      }

      // Gray out the marker for all teams
      if (checkpointMarkersRef.current[checkpointId]) {
        const marker = checkpointMarkersRef.current[checkpointId];
        marker.setIcon({
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 13,
          fillColor: "#6b7280",
          fillOpacity: 1,
          strokeColor: "#374151",
          strokeWeight: 2,
        });
      }
    };
    socket.on("checkpointCollected", handler);
    return () => socket.off("checkpointCollected", handler);
  }, [showToast]);

  // -----------------------------
  // Race started (this team)
  // -----------------------------
  useEffect(() => {
    const handler = ({ startTime }) => {
      startTimeRef.current = startTime;
      setRaceStarted(true);
      showToast("GO! Your race has started!", 3000);
    };
    socket.on("raceStarted", handler);
    return () => socket.off("raceStarted", handler);
  }, [showToast]);

  // -----------------------------
  // Race finished (this team)
  // -----------------------------
  useEffect(() => {
    const handler = ({ rank, elapsedMs: ms }) => {
      setRaceFinished(true);
      setFinishData({ rank, elapsedMs: ms });
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      showToast(`You finished! Rank #${rank} — Time: ${formatElapsed(ms)}`, 8000);
    };
    socket.on("raceFinished", handler);
    return () => socket.off("raceFinished", handler);
  }, [showToast]);

  // -----------------------------
  // Leaderboard updates
  // -----------------------------
  useEffect(() => {
    const handler = (board) => {
      if (redirectingRef.current) return;
      setLeaderboard(Array.isArray(board) ? board : []);

      // Reconcile own collected checkpoints from leaderboard
      // We can't identify by teamId here easily — rely on checkpointCollected events
    };
    socket.on("leaderboardUpdate", handler);
    return () => socket.off("leaderboardUpdate", handler);
  }, []);

  // -----------------------------
  // Game ended (all teams finished or admin ended)
  // -----------------------------
  useEffect(() => {
    const handler = ({ results }) => {
      if (gameEndedRef.current) return;
      gameEndedRef.current = true;

      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }

      setGameEndResults(results || []);

      localStorage.setItem("lastGameResult", JSON.stringify({ ts: Date.now(), results }));

      setTimeout(() => {
        redirectingRef.current = true;
        window.location.replace("/lobby");
      }, 12000);
    };
    socket.on("gameEnded", handler);
    return () => socket.off("gameEnded", handler);
  }, []);

  // -----------------------------
  // Session invalid
  // -----------------------------
  useEffect(() => {
    const handler = () => {
      if (redirectingRef.current || gameEndedRef.current) return;
      window.location.replace("/login");
    };
    socket.on("sessionInvalid", handler);
    socket.on("forceLogout", handler);
    return () => {
      socket.off("sessionInvalid", handler);
      socket.off("forceLogout", handler);
    };
  }, []);

  // -----------------------------
  // Screen wake lock — keep screen on while map is active
  // -----------------------------
  useEffect(() => {
    if (!('wakeLock' in navigator)) return; // not supported (older browsers)

    let wakeLock = null;

    const acquire = async () => {
      try {
        wakeLock = await navigator.wakeLock.request('screen');
      } catch (err) {
        console.warn('Wake lock request failed:', err);
      }
    };

    // Re-acquire after the page becomes visible again (lock is auto-released on hide)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      wakeLock?.release().catch(() => {});
    };
  }, []);

  // -----------------------------
  // Heartbeat
  // -----------------------------
  useEffect(() => {
    if (!userId) return;
    const t = setInterval(() => {
      if (!redirectingRef.current && socket.connected) socket.emit("heartbeat");
    }, 4000);
    return () => clearInterval(t);
  }, [userId]);

  // -----------------------------
  // Other player position updates
  // -----------------------------
  useEffect(() => {
    const handler = ({ userId: pid, username, lat, lng }) => {
      if (redirectingRef.current || !pid) return;
      const idStr = pid.toString();

      setPlayers(prev => ({ ...prev, [idStr]: { username, lat, lng } }));

      if (mapReady && googleMap.current) {
        if (!markersRef.current[idStr]) {
          markersRef.current[idStr] = new window.google.maps.Marker({
            map: googleMap.current,
            position: { lat, lng },
            label: username || "",
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#1e90ff",
              fillOpacity: 1,
              strokeWeight: 1,
            }
          });
        } else {
          markersRef.current[idStr].setPosition({ lat, lng });
        }
      }
    };
    socket.on("playerPositionUpdate", handler);
    return () => socket.off("playerPositionUpdate", handler);
  }, [mapReady]);

  // -----------------------------
  // Visibility change (phone wake)
  // -----------------------------
  useEffect(() => {
    let sleepTimer = null;
    const onVis = () => {
      if (redirectingRef.current || gameEndedRef.current) return;

      if (document.visibilityState === "hidden") {
        sleepTimer = setTimeout(() => {
          const uid = userIdRef.current;
          if (!uid) return;
          socket.emit("clientSleeping", { userId: uid, reason: "Backgrounded too long" });
          window.location.replace("/login");
        }, 8000);
      } else {
        if (sleepTimer) { clearTimeout(sleepTimer); sleepTimer = null; }

        if (watchIdRef.current) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }

        (async () => {
          try {
            const res = await axios.get("/api/my-approval-status", { withCredentials: true });
            setGameInfo(res.data);
            gameIdRef.current = res.data?.gameId || null;
            const uid = userIdRef.current;
            const gid = gameIdRef.current;
            if (uid) socket.emit("identify", uid);
            if (gid) socket.emit("joinGameRoom", gid);
          } catch {
            window.location.replace("/lobby");
          }
        })();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (sleepTimer) clearTimeout(sleepTimer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // -----------------------------
  // Map init — runs immediately, no GPS needed
  // -----------------------------
  useEffect(() => {
    if (!gameInfo?.gameId || !userId) return;
    if (googleMap.current) return; // already initialized
    if (!mapRef.current) return;

    // Initialize at Colonial Williamsburg center — re-centered on first GPS fix
    const DEFAULT_CENTER = { lat: 37.2707, lng: -76.7075 };

    googleMap.current = new window.google.maps.Map(mapRef.current, {
      center: DEFAULT_CENTER,
      zoom: 17,
      mapTypeId: "terrain",
    });

    socket.emit("identify", userId);
    socket.emit("joinGameRoom", gameInfo.gameId);

    gpsCenteredRef.current = false;
    setMapReady(true);
  }, [gameInfo?.gameId, userId]);

  // -----------------------------
  // GPS watch — position updates only, map already initialized
  // -----------------------------
  useEffect(() => {
    if (!gameInfo?.gameId || !userId) return;
    if (redirectingRef.current) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        if (redirectingRef.current || gameEndedRef.current) return;

        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const myLoc = { lat, lng };
        setMyPosition(myLoc);

        socket.emit("gpsAlive");

        // Re-center map on first real GPS fix
        if (googleMap.current && !gpsCenteredRef.current) {
          googleMap.current.setCenter(myLoc);
          gpsCenteredRef.current = true;
        }

        // Send position — server handles checkpoint auto-collection
        socket.emit("playerMove", {
          userId,
          gameId: gameInfo.gameId,
          lat,
          lng,
        });
      },
      (err) => console.error("GPS error:", err),
      { enableHighAccuracy: true }
    );

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    };
  }, [gameInfo?.gameId, userId]);

  // -----------------------------
  // Navigation arrow target
  // -----------------------------
  const navTarget = useMemo(() => {
    if (!myPosition || checkpoints.length === 0 || raceFinished) return null;

    const startCp  = checkpoints.find(c => c.type === 'start');
    const finishCp = checkpoints.find(c => c.type === 'finish');
    const regularCps = checkpoints.filter(c => c.type === 'checkpoint');
    const uncollected = regularCps.filter(c => !collectedIds.has(c._id));

    let target, label, accentColor;

    if (!raceStarted) {
      // Point to start
      if (!startCp) return null;
      target = startCp;
      label = "START";
      accentColor = "#22c55e";
    } else if (uncollected.length > 0) {
      // Point to nearest uncollected regular checkpoint
      let nearest = null, nearestDist = Infinity;
      for (const cp of uncollected) {
        const d = haversineM(myPosition.lat, myPosition.lng, cp.lat, cp.lng);
        if (d < nearestDist) { nearestDist = d; nearest = cp; }
      }
      if (!nearest) return null;
      target = nearest;
      label = nearest.name;
      accentColor = "#3b82f6";
    } else {
      // All regular collected — point to finish
      if (!finishCp) return null;
      target = finishCp;
      label = "FINISH";
      accentColor = "#f59e0b";
    }

    const dist = haversineM(myPosition.lat, myPosition.lng, target.lat, target.lng);
    const bearing = bearingTo(myPosition, target);

    return {
      label,
      bearing,
      dist,
      distLabel: formatDist(dist),
      cardinal: cardinalDir(bearing),
      accentColor
    };
  }, [myPosition, checkpoints, collectedIds, raceStarted, raceFinished]);

  // -----------------------------
  // My progress derived from leaderboard
  // -----------------------------
  const myTeamEntry = leaderboard.find(t => {
    // Match by teamId stored in gameInfo
    return gameInfo?.teamId && t.teamId === gameInfo.teamId?.toString();
  });
  const myCollected = myTeamEntry?.collected ?? (gameInfo?.checkpointsCollected || 0);
  const myTotal = myTeamEntry?.total ?? (gameInfo?.totalCheckpoints || 0);

  // -----------------------------
  // Game ended overlay
  // -----------------------------
  if (gameEndResults) {
    return (
      <div style={{
        position: "fixed", inset: 0, background: "#111827",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", color: "white", padding: "20px", zIndex: 99999
      }}>
        <h1 style={{ fontSize: "28px", marginBottom: "20px" }}>Race Complete!</h1>
        <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: "400px" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #374151" }}>
              <th style={{ padding: "8px", textAlign: "left" }}>Rank</th>
              <th style={{ padding: "8px", textAlign: "left" }}>Team</th>
              <th style={{ padding: "8px", textAlign: "right" }}>Time</th>
            </tr>
          </thead>
          <tbody>
            {gameEndResults.map((r, i) => (
              <tr key={r.teamId || i} style={{ borderBottom: "1px solid #374151" }}>
                <td style={{ padding: "8px" }}>#{r.rank || i + 1}</td>
                <td style={{ padding: "8px" }}>{r.teamName}</td>
                <td style={{ padding: "8px", textAlign: "right" }}>
                  {r.elapsedMs ? formatElapsed(r.elapsedMs) : "DNF"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ marginTop: "20px", color: "#9ca3af" }}>Returning to lobby…</p>
      </div>
    );
  }

  // -----------------------------
  // Race finished overlay (waiting for others)
  // -----------------------------
  const finishOverlay = raceFinished && finishData && !gameEndResults ? (
    <div style={{
      position: "absolute", top: "50%", left: "50%",
      transform: "translate(-50%, -50%)",
      background: "rgba(0,0,0,0.9)", color: "white",
      padding: "24px 32px", borderRadius: "12px",
      zIndex: 9998, textAlign: "center",
      border: "2px solid gold"
    }}>
      <div style={{ fontSize: "28px", marginBottom: "8px" }}>You Finished!</div>
      <div style={{ fontSize: "20px", color: "gold" }}>Rank #{finishData.rank}</div>
      <div style={{ fontSize: "18px", marginTop: "6px" }}>{formatElapsed(finishData.elapsedMs)}</div>
      <div style={{ marginTop: "10px", color: "#9ca3af", fontSize: "14px" }}>
        Waiting for other teams…
      </div>
    </div>
  ) : null;

  return (
    <div style={{ position: "relative", height: "100vh", display: "flex", flexDirection: "column" }}>

      {/* Toast */}
      {toastMessage && (
        <div style={{
          position: "absolute", top: "70px", left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.85)", color: "lime",
          padding: "10px 18px", borderRadius: "8px",
          zIndex: 9999, fontSize: "16px", textAlign: "center",
          border: "2px solid lime", pointerEvents: "none",
          maxWidth: "90vw"
        }}>
          {toastMessage}
        </div>
      )}

      {/* Top HUD */}
      <div style={{
        background: "#111827", color: "white",
        padding: "10px 14px", display: "flex",
        justifyContent: "space-between", alignItems: "center",
        zIndex: 100, flexShrink: 0
      }}>
        <div>
          <div style={{ fontWeight: "bold", fontSize: "15px" }}>
            {gameInfo?.gameName || "Race"}
          </div>
          <div style={{ fontSize: "13px", color: "#9ca3af" }}>
            {gameInfo?.teamName || "Your Team"}
          </div>
        </div>

        {/* Checkpoint progress */}
        <div style={{
          background: "#1f2937", padding: "6px 12px",
          borderRadius: "8px", textAlign: "center"
        }}>
          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#10b981" }}>
            {myCollected} / {myTotal}
          </div>
          <div style={{ fontSize: "11px", color: "#6b7280" }}>checkpoints</div>
        </div>

        {/* Timer */}
        <div style={{ textAlign: "right" }}>
          {!raceStarted ? (
            <div style={{ color: "#22c55e", fontSize: "13px", fontWeight: "bold" }}>Drive to START</div>
          ) : (
            <div style={{ fontSize: "20px", fontWeight: "bold", fontFamily: "monospace" }}>
              {raceFinished && finishData
                ? formatElapsed(finishData.elapsedMs)
                : formatElapsed(elapsedMs)}
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div id="live-map-container" ref={mapRef} style={{ flex: 1 }}></div>

      {/* Navigation arrow */}
      {navTarget && (
        <div style={{
          position: "absolute",
          bottom: "16px",
          left: "16px",
          background: "rgba(15,23,42,0.92)",
          border: `2px solid ${navTarget.accentColor}`,
          borderRadius: "14px",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          zIndex: 200,
          minWidth: "130px",
          pointerEvents: "none"
        }}>
          {/* Rotating arrow */}
          <div style={{
            fontSize: "34px",
            lineHeight: 1,
            transform: `rotate(${navTarget.bearing}deg)`,
            transformOrigin: "center",
            color: navTarget.accentColor,
            transition: "transform 0.4s ease"
          }}>
            ↑
          </div>
          {/* Distance + label */}
          <div>
            <div style={{
              fontSize: "22px",
              fontWeight: "bold",
              color: "white",
              lineHeight: 1.1
            }}>
              {navTarget.distLabel}
            </div>
            <div style={{
              fontSize: "12px",
              color: navTarget.accentColor,
              fontWeight: "bold",
              marginTop: "2px"
            }}>
              {navTarget.label}
            </div>
            <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "1px" }}>
              {navTarget.cardinal}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard toggle button */}
      <button
        onClick={() => setShowLeaderboard(v => !v)}
        style={{
          position: "absolute", bottom: "16px", right: "16px",
          background: "#1f2937", color: "white",
          border: "2px solid #374151", borderRadius: "8px",
          padding: "10px 14px", fontSize: "14px",
          cursor: "pointer", zIndex: 200
        }}
      >
        {showLeaderboard ? "Hide" : "Standings"}
      </button>

      {/* Leaderboard panel */}
      {showLeaderboard && (
        <div style={{
          position: "absolute", bottom: "60px", right: "16px",
          background: "#111827", color: "white",
          border: "2px solid #374151", borderRadius: "10px",
          padding: "12px", minWidth: "220px", zIndex: 200
        }}>
          <div style={{ fontWeight: "bold", marginBottom: "8px", fontSize: "14px" }}>
            Live Standings
          </div>
          {leaderboard.length === 0 ? (
            <div style={{ color: "#6b7280", fontSize: "13px" }}>No teams yet</div>
          ) : (
            leaderboard
              .slice()
              .sort((a, b) => {
                if (a.finished && !b.finished) return -1;
                if (!a.finished && b.finished) return 1;
                if (a.finished && b.finished) return a.elapsedMs - b.elapsedMs;
                return b.collected - a.collected;
              })
              .map((t, i) => (
                <div key={t.teamId} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "5px 0", borderBottom: "1px solid #374151",
                  fontSize: "13px"
                }}>
                  <div>
                    <span style={{ color: "#9ca3af", marginRight: "6px" }}>#{i + 1}</span>
                    {t.teamName}
                    {!t.raceStarted && (
                      <span style={{ color: "#f59e0b", fontSize: "11px", marginLeft: "6px" }}>
                        (waiting)
                      </span>
                    )}
                  </div>
                  <div style={{ color: t.finished ? "#10b981" : "#facc15" }}>
                    {t.finished
                      ? formatElapsed(t.elapsedMs)
                      : `${t.collected}/${t.total}`}
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {finishOverlay}
    </div>
  );
}
